// @ts-check
// import "./elevate-facets.js";
import "./elevate-pagination.js";

/**
 * @typedef {Object} ElevateConfig
 * @property {string} moneyFormat - The money format string.
 * @property {boolean} showSoldOutBadge - Whether to show the sold out badge.
 * @property {boolean} showDiscount - Whether to show the discount.
 */

const SRC_SETS = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400];

/** @type {ElevateConfig} */ // @ts-ignore
const ELEVATE_CONFIG = window.elevateConfig || {};

function formatMoney(
  /** @type {number | string} */ cents,
  /** @type {string | undefined} */ format = undefined,
) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || ELEVATE_CONFIG.moneyFormat;

  // @ts-ignore
  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  // @ts-ignore
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  var match = formatString.match(placeholderRegex);

  if (match) {
    switch (match[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    }
  }

  return formatString.replace(placeholderRegex, value);
}

class ElevatePage extends HTMLElement {
  static get observedAttributes() {
    return ["handle", "section-id", "products-per-page", "currency-symbol"];
  }

  /** @type {string | null | undefined} */
  handle = null;

  /** @type {string | null | undefined} */
  type = null;

  /** @type {string | null | undefined} */
  sectionId = null;
  urlParams = new URLSearchParams(window.location.search);

  /** @type {string | null | undefined} */
  urlRoot = "/";

  /** @type {any[]} */
  products = [];

  /** @type {number} */
  productsPerPage = 48;

  /** @type {boolean} */
  testMode = false;

  constructor() {
    super();
  }

  connectedCallback() {
    const type = this.getAttribute("type");

    if (!type) {
      console.error("No type provided");
      return;
    }

    try {
      if (typeof Storage !== "undefined") {
        const storedTestMode = localStorage.getItem("elevate_test_mode");
        if (storedTestMode) {
          this.testMode = storedTestMode === "1";
        }
      }
    } catch (error) {
      console.error("LocalStorage not available.");
    }

    window.addEventListener("popstate", () => {
      this.urlParams = new URLSearchParams(window.location.search);
      this.fetchProducts();
    });

    // @ts-ignore
    this.urlRoot = window.Shopify?.routes?.root || "/";

    if (type === "search") {
      this.handle = this.urlParams.get("q");
    } else {
      const handle = this.getAttribute("handle");
      this.handle =
        typeof handle === "string" && handle.indexOf("/collections") > -1
          ? handle.substring(handle.indexOf("/collections") + 12)
          : handle;
    }

    this.type = type;
    this.sectionId = this.getAttribute("section-id");
    this.collectionMobileLayout = this.getAttribute("collection-mobile-layout");
    this.collectionDesktopLayout = this.getAttribute(
      "collection-desktop-layout",
    );

    const productsPerPage =
      this.getAttribute("products-per-page") &&
      !isNaN(Number(this.getAttribute("products-per-page")))
        ? Number(this.getAttribute("products-per-page"))
        : 48;
    this.productsPerPage = Math.min(Math.abs(productsPerPage), 48);

    // Preview mode
    if (this.urlParams.get("PREVIEW") === "true") {
      this.urlParams.delete("PREVIEW");
      this.urlParams.set("viewId", "preview");
    }

    // Campaign ID
    if (this.urlParams.get("utm_campaign")) {
      this.urlParams.set(
        "campaignTag",
        String(this.urlParams.get("utm_campaign")),
      );
    }

    this.setupListeners();
    this.fetchProducts();
  }

  scrollToTop() {
    try {
      const scrollToProductList = () =>
        document
          .querySelector(".collection")
          ?.scrollIntoView({ block: "start", behavior: "smooth" });
      if ("requestIdleCallback" in window) {
        requestIdleCallback(scrollToProductList, { timeout: 500 });
      } else {
        requestAnimationFrame(scrollToProductList);
      }
    } catch (error) {
      console.error("Error scrolling to top:", error);
    }
  }

  setupListeners() {
    // Listen for facet updates
    document.addEventListener("elevate:updateFilters", (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      this.urlParams.delete("page");

      switch (detail.type) {
        case "RANGE":
          if (!detail.value) {
            this.urlParams.delete(`f.${detail.facetId}.min`);
            this.urlParams.delete(`f.${detail.facetId}.max`);
          } else {
            const minValue =
              typeof detail.value === "object" ? detail.value.min : 0;
            const maxValue =
              typeof detail.value === "object"
                ? detail.value.max
                : detail.value;

            this.urlParams.set(`f.${detail.facetId}.min`, minValue);
            this.urlParams.set(`f.${detail.facetId}.max`, maxValue);
          }
          break;
        default:
          let facetValue = this.urlParams.get(`f.${detail.facetId}`) || "";
          if (!detail.add) {
            facetValue = facetValue
              .split("%7C")
              .join("|")
              .split("|")
              .filter((value) => value !== detail.value)
              .join("|");
          } else {
            facetValue += (facetValue ? "|" : "") + detail.value;
          }

          if (facetValue.length > 0) {
            this.urlParams.set(`f.${detail.facetId}`, facetValue);
          } else {
            this.urlParams.delete(`f.${detail.facetId}`);
          }
      }

      const urlParamsString = this.urlParams.toString();
      const urlParamObject = Object.fromEntries(this.urlParams);
      history.pushState(
        urlParamObject,
        "",
        urlParamsString ? `?${urlParamsString}` : location.pathname,
      );
      this.fetchProducts({ updateFacets: true });
    });

    // Listen for sort updates
    document.addEventListener("elevate:updateSort", (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      this.urlParams.delete("page");
      this.urlParams.set("sort", detail.id);

      const urlParamsString = this.urlParams.toString();

      history.pushState(
        null,
        "",
        urlParamsString ? `?${urlParamsString}` : location.pathname,
      );

      this.fetchProducts({ updateFacets: false });
    });

    // Listen for page changes
    document.addEventListener("elevate:updatePage", (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      this.urlParams.set(`page`, detail.page);
      const urlParamsString = this.urlParams.toString();
      history.pushState(
        null,
        "",
        urlParamsString ? `?${urlParamsString}` : location.pathname,
      );
      this.fetchProducts({ updateFacets: false, scrollToTop: true });
    });

    // Observe product clicks
    this.addEventListener("click", (event) => {
      if (
        // @ts-ignore
        !window.elevate ||
        !event.target ||
        !(event.target instanceof Element)
      ) {
        return;
      }

      const anchor =
        event.target.nodeName === "A"
          ? event.target
          : event.target.closest("a");

      const productId = anchor?.getAttribute("data-product-id");
      if (!productId) {
        return;
      }

      const product = this.products.find((p) => p.key === productId);
      if (product) {
        // @ts-ignore
        window.elevate.notify.click(product.ticket);
      }
    });
  }

  async waitForElevate() {
    const sleep = (ms = 1000) => {
      return new Promise((resolve) => setTimeout(resolve, ms));
    };

    for (let i = 1; i < 5; i++) {
      // @ts-ignore
      if (window.elevate && window.elevate.query) {
        return i;
      }
      await sleep(i * 500);
    }

    throw new Error("Elevate not initialized");
  }

  async fetchProducts({ updateFacets = true, scrollToTop = false } = {}) {
    const elevateProductsElement = this.querySelector("#elevate-products");

    try {
      await this.waitForElevate();

      if (!this.handle || !this.sectionId) {
        throw new Error(
          `No ${!this.handle ? "handle" : "section ID"} provided to elevate-page`,
        );
      }

      if (elevateProductsElement) {
        elevateProductsElement.classList.add("loading");
      }

      let results;

      /** @type {any} */
      const decoratedOptions = {};
      if (this.testMode) {
        decoratedOptions.notify = false;
      }

      if (this.type === "search") {
        // @ts-ignore
        results = await window.elevate.query.searchPage(
          {
            ...Object.fromEntries(this.urlParams),
            skip:
              Number(this.urlParams.get("page") || 1) * this.productsPerPage -
              this.productsPerPage,
            limit: this.productsPerPage,
            presentCustom: [
              "filter_color",
              "color_hex",
              "badge",
              "image_alt_text",
            ],
            ...decoratedOptions,
          },
          {
            primaryList: {
              include: true,
            },
          },
        );
      } else {
        // @ts-ignore
        results = await window.elevate.query.landingPage({
          ...Object.fromEntries(this.urlParams),
          skip:
            Number(this.urlParams.get("page") || 1) * this.productsPerPage -
            this.productsPerPage,
          limit: this.productsPerPage,
          pageReference: this.handle,
          presentCustom: [
            "filter_color",
            "color_hex",
            "badge",
            "image_alt_text",
          ],
          ...decoratedOptions,
        });
      }

      // Update facets
      if (updateFacets) {
        document.dispatchEvent(
          new CustomEvent("elevate:facets", {
            detail: results.primaryList.facets,
          }),
        );

        document.dispatchEvent(
          new CustomEvent("elevate:sort", {
            detail: results.primaryList.sort,
          }),
        );
      }

      this.products = results.primaryList.productGroups
        .map((/** @type {any} */ group) =>
          // @ts-ignore
          group.products.map((p) => ({ key: p.key, ticket: p.ticket })),
        )
        .flat();

      // Update suggested terms
      const phraseSuggestions = this.querySelector(".elevate-suggested-terms");
      if (phraseSuggestions) {
        const suggestions =
          results.didYouMean && results.didYouMean.length
            ? results.didYouMean
            : results.relatedSearches;
        if (suggestions && suggestions.length > 0) {
          phraseSuggestions.innerHTML = `
            <span>
              ${
                phraseSuggestions.getAttribute("data-context") ||
                "Search suggestions:"
              }
            </span>
            <span>
              ${suggestions
                .map(
                  (/** @type {any} */ term) => `
                  <a class="link" href="${
                    location.pathname
                  }?q=${encodeURIComponent(term.q)}&origin=${
                    results.didYouMean && results.didYouMean.length
                      ? "DID_YOU_MEAN"
                      : "RELATED_SEARCHES"
                  }&_v=1.0" class="elevate-suggested-term">
                    ${term.q}
                  </a>
              `,
                )
                .join(", ")}
            </span>
          `;
        } else {
          phraseSuggestions.innerHTML = "";
        }
      }

      // Update pagination and totals
      const totals = this.querySelectorAll(".elevate-product-count");
      for (const total of totals) {
        let ctx = total.getAttribute("data-context") || "[count]";
        if (results.autoCorrect?.q && ctx.includes(`"${results.q}"`)) {
          ctx = ctx.replace(`"${results.q}"`, `"${results.autoCorrect.q}"`);
          total.setAttribute("data-context", ctx);
        }
        total.innerHTML = ctx.replace("[count]", results.primaryList.totalHits);
      }

      // Update query parameter q if it was corrected
      if (results.autoCorrect?.q) {
        this.urlParams.set("q", results.autoCorrect.q);
      }

      if (!elevateProductsElement) {
        throw new Error("No product container found");
      }

      const productListContainer = document.createElement("product-list");
      productListContainer.id = `product-list-${this.sectionId}`;
      productListContainer.className = "product-list elevate-product-list";
      productListContainer.setAttribute(
        "collection-mobile-layout",
        this.collectionMobileLayout || "medium",
      );
      productListContainer.setAttribute(
        "collection-desktop-layout",
        this.collectionDesktopLayout || "medium",
      );

      results.primaryList.productGroups.forEach((/** @type {any} */ group) => {
        const product = group.products[0];
        const handle = product.link.replace(/\/products\//, "").split("?")[0];
        const productElement = document.createElement("product-card");

        let displayImage = "";
        if (
          product.imageInfo.images?.length &&
          product.imageInfo.images[0].sources?.length
        ) {
          displayImage = `<img src="${
            product.imageInfo.images[0].sources[0].url
          }" alt="${product.title} - Type: product" srcset="${SRC_SETS.map(
            (width) =>
              `${product.imageInfo.images[0].sources[0].url}&width=${width} ${width}w`,
          ).join(
            ", ",
          )}" sizes="(max-width: 699px) 74vw, (max-width: 999px) 38vw, calc((100vw - 96px) / 3 - (24px / 3 * 2))" draggable="false" class="product-card__image product-card__image--primary  aspect-tall">`;
        }

        // const displayImage = `<img src="${
        //   product.imageInfo.images[0].sources[0].url
        // }" alt="${product.title} - Type: product" srcset="${SRC_SETS.map(
        //   (width) =>
        //     `${product.imageInfo.images[0].sources[0].url}&width=${width} ${width}w`,
        // ).join(
        //   ", ",
        // )}" sizes="(max-width: 699px) 74vw, (max-width: 999px) 38vw, calc((100vw - 96px) / 3 - (24px / 3 * 2))" draggable="false" class="product-card__image product-card__image--primary  aspect-tall">`;

        productElement.setAttribute("reveal-on-scroll", "true");
        productElement.setAttribute("handle", handle);
        productElement.className = "product-card";

        let hasComingSoonBadge = false;
        try {
          hasComingSoonBadge = product.custom.badge?.some(
            (/** @type {{ label: string; }} */ badge) =>
              badge.label?.toLowerCase() === "coming soon",
          );
        } catch (error) {
          console.error("Error retrieving hasComingSoon badge");
        }

        if (hasComingSoonBadge) {
          productElement.classList.add("coming-soon");
        }

        const listPrice = product.listPrice.min;
        const sellingPrice = product.sellingPrice.min;
        const isSale = sellingPrice < listPrice;

        const firstInStockVariant = product.variants.find(
          // @ts-ignore
          (variant) => variant.inStock,
        );

        if (!firstInStockVariant) {
          return;
        }

        const badgesHtml = (() => {
          const badges = [];
          if (isSale && ELEVATE_CONFIG.showDiscount) {
            badges.push(
              `<on-sale-badge class="badge badge--on-sale">${Math.round(
                ((listPrice - sellingPrice) / listPrice) * 100,
              )}%</on-sale-badge>`,
            );
          }

          if (product.custom.badge?.length) {
            for (const badge of product.custom.badge) {
              const badgeValue = badge.label?.toLowerCase().replace(/ /g, "-");
              badges.push(
                `<span class="badge badge--custom badge--${badgeValue}" style="background-color:#d6c9b1;color:#ffffff;">${badge.label}</span>`,
              );
            }
          }

          if (!badges.length) {
            return "";
          }
          return `<badge-list class="badge-list badge-list--vertical">${badges.join(
            "",
          )}</badge-list>`;
        })();

        const colorSwatches = group.products
          // @ts-ignore
          .filter((p) => p.swatch?.type === "COLORS")
          // @ts-ignore
          .map((p) => {
            const image =
              p.imageInfo?.images?.length > 1
                ? p.imageInfo.images[1]
                : p.imageInfo?.images[0];
            return {
              color: p.custom?.color_hex?.[0].label || p.swatch.colors?.[0],
              colorName: p.custom?.filter_color?.value || "",
              link: `${this.urlRoot}${p.link.replace(/\/products\//, "products/")}`,
              key: p.key,
              title: p.title,
              image: image?.sources[0]?.url || "",
              width: image?.sources[0]?.width || 0,
              height: image?.sources[0]?.height || 0,
              srcset: SRC_SETS.map(
                (width) => `${image?.sources[0]?.url}&width=${width} ${width}w`,
              ).join(", "),
            };
          });

        let colorSwatchesHtml = "";
        if (colorSwatches.length > 1) {
          productElement.classList.add("has-color-swatches");
          colorSwatchesHtml = colorSwatches
            .map(
              (/** @type {any} */ swatch) => `
            <a data-product-id="${swatch.key}" href="${
              swatch.link
            }" class="product-colors__color-label product-colors__balls" title="${
              product.title
            }">
              <div class="product-colors__ball" style="background-color: ${
                swatch.color
              };"></div>
              <span class="visually-hidden">${
                swatch.colorName || swatch.color
              }</span>
              <div class="color-hover-image">
                <div class="product-card__figure"><img src="${
                  swatch.image
                }" alt="${swatch.title} - Type: product" srcset="${
                  swatch.srcset
                }" sizes="(max-width: 699px) 74vw, (max-width: 999px) 38vw, calc((100vw - 96px) / 3 - (24px / 3 * 2))" draggable="false" class="product-card__image product-card__image--primary  aspect-tall"></div>
              </div>
            </a>
          `,
            )
            .join("");
        }

        // @ts-ignore
        const productSlides = product.imageInfo.images.map((image, i) => {
          if (!image.sources?.length) {
            return "";
          }

          const source = image.sources[0];
          const mediaType = image.custom?.type || "MediaImage";
          const videoUrl =
            mediaType === "Video"
              ? image.custom?.videoUrl || image.custom?.orgVideoUrl
              : null;

          const srcSet = SRC_SETS.map(
            (width) => `${source.url}&width=${width} ${width}w`,
          ).join(", ");

          if (videoUrl) {
            const videoWidth = Number(
              image.custom.videoWidth || image.custom.orgVideoWidth,
            );
            const videoHeight = Number(
              image.custom.videoHeight || image.custom.orgVideoHeight,
            );
            const aspectRatio =
              videoWidth && videoHeight
                ? (videoWidth / videoHeight).toFixed(3)
                : "0.588";
            return `
              <slide data-type="" class="${i === 0 ? "active " : ""}no-switch">
                <video-media autoplay style="--aspect-ratio: ${aspectRatio};height:100%;">
                  <video playsinline="true" class="object-cover" muted="muted" loop="loop" preload="metadata" aria-label="${
                    product.title
                  } - Type: video" poster="${source.url}">
                    <source src="${videoUrl}" type="${image.custom.videoMimeType || image.custom.orgVideoMimeType}">
                    <img alt="${product.title} - Type: video" src="${source.url}">
                  </video>
                </video-media>
              </slide>
            `;
          }
          return `
            <slide data-type="" class="${i === 0 ? "active " : ""}no-switch">
              <img src="${source.url}&width=${source.width}" alt="${
                product.title
              }" width="${source.width}" height="${
                source.height
              }" srcset="${srcSet}" sizes="(max-width: 699px) calc(100vw / 2), (max-width: 999px) calc(100vw / 0 - 64px), calc((100vw - 96px) / 3 - (24px / 3 * 2))" draggable="true" class="product-card__image ${
                i === 0 ? "product-card__image--primary" : ""
              } aspect-tall">
            </slide>
          `;
        });

        productElement.innerHTML = `
          <div class="product-card__figure">
            ${badgesHtml}
            <product-slideshow class="product-slideshow slideshow-${product.key}">
              ${productSlides.join("")}
              <slideswiper>
                <a href="${
                  this.urlRoot
                }products/${handle}" class="product-card__media" data-product-id="${
                  product.key
                }" draggable="true"></a>
              </slideswiper>
              ${
                productSlides.length > 1
                  ? `
                  <previous class="fadeout">
                    <button class="product-gallery__prev-button circle-button circle-button--sm" type="button" aria-label="Previous">
                      <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
                        <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"></path>
                      </svg>
                    </button>
                  </previous>
                  <next class="fadeout">
                    <button class="product-gallery__next-button circle-button circle-button--sm" type="button" aria-label="Next">
                      <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
                        <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"></path>
                      </svg>
                    </button>
                  </next>
                `
                  : ""
              }
              <slidespacer>
                ${displayImage}
              </slidespacer>
            </product-slideshow>

            <button type="button" aria-controls="product-quick-buy-${
              this.sectionId
            }__main---${product.key}" class="product-card__quick-add-button">
              <span class="sr-only">Choose options</span><svg aria-hidden="true" fill="none" focusable="false" width="24" class="icon icon-cart" viewBox="0 0 24 24"><path d="M4.75 8.25A.75.75 0 0 0 4 9L3 19.125c0 1.418 1.207 2.625 2.625 2.625h12.75c1.418 0 2.625-1.149 2.625-2.566L20 9a.75.75 0 0 0-.75-.75H4.75Zm2.75 0v-1.5a4.5 4.5 0 0 1 4.5-4.5v0a4.5 4.5 0 0 1 4.5 4.5v1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            </button>

            <quick-buy-modal handle="${firstInStockVariant.link.replace(
              /\/products\//,
              "",
            )}" class="quick-buy-modal modal" id="product-quick-buy-${
              this.sectionId
            }__main---${
              product.key
            }" role="dialog" aria-modal="true" ly-is-observing="true"></quick-buy-modal>
          </div>
          <div class="product-card__info empty:hidden">
            <div class="v-stack justify-items-center gap-2">
              <div class="v-stack justify-items-center gap-1">
                <a href="${product.link}" class="product-title">${
                  product.title
                }</a>
                <price-list class="price-list ">
                  <sale-price class="${isSale ? "text-on-sale" : "text-subdued"}">
                    <span class="sr-only">Sale price</span>${formatMoney(
                      sellingPrice * 100,
                    )}
                  </sale-price>
                  ${
                    isSale
                      ? `<compare-at-price class="text-subdued line-through"><span class="sr-only">Regular price</span>${formatMoney(
                          listPrice * 100,
                        )}</compare-at-price>`
                      : ``
                  }
                </price-list>
              </div>
            </div>
          </div>
          ${
            colorSwatchesHtml
              ? `<div id="product-colors-${product.key}" class="product-colors">${colorSwatchesHtml}</div>`
              : ""
          }
        `;

        productListContainer.appendChild(productElement);
      });

      elevateProductsElement.classList.remove("loading");
      elevateProductsElement.innerHTML = "";
      elevateProductsElement.appendChild(productListContainer);

      if (scrollToTop) {
        this.scrollToTop();
      }

      document.dispatchEvent(
        new CustomEvent("elevate:pagination", {
          detail: {
            current: Number(this.urlParams.get("page") || 1),
            totalProducts: results.primaryList.totalHits,
            productsPerPage: this.productsPerPage,
          },
        }),
      );
    } catch (error) {
      if (elevateProductsElement) {
        elevateProductsElement.innerHTML = `
          <div>
            <h3>Unfortunately something went wrong...</h3><br />
            <p>
              This may be caused by browser cache or an outdated browser. If the problem persists after clearing
              cache or updating the browser, please contact support and include the error message below.
            </p><br />
            <p>
              Our apologies for this inconvenience. 
            </p><br />
            <div style="background-color: #f0f0f0; color: #880000; padding: 20px;">${error}</div>
          </div>
        `;
      }
      console.error("Elevate error", error);
      return;
    }
  }
}

if (!window.customElements.get("elevate-page")) {
  customElements.define("elevate-page", ElevatePage);
}
